/**
 * Ready-gate: когда чат можно показывать композер / слать сообщения.
 * Backend provision (room/space) + локальный Matrix join/sync.
 */
import type { IChat, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'
import { isTopicsModeChat } from '@/modules/chat-matrix-fast/utils/topics'
import {
  getMatrixClient,
  isMatrixClientActive,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { getMembershipStatus } from '@/modules/chat-matrix-fast/services/matrixMembershipState'
import { membershipFailureMessage } from '@/modules/chat-matrix-fast/services/membershipProtocol'

export type ChatReadyStatus = 'creating' | 'joining' | 'ready' | 'failed' | 'degraded'

export interface ChatReadyState {
  status: ChatReadyStatus
  canCompose: boolean
  message: string
  roomId?: string
}

/** Backend: room готов (для topics — ещё Space и не provisioning). */
export function isBackendRoomReady(
  chat: Pick<IChat, 'matrix_room_id' | 'space_room_id' | 'discussion_mode' | 'transition_status'> | null | undefined,
): boolean {
  if (!chat?.matrix_room_id?.trim()) {
    return false
  }
  if (chat.transition_status === 'failed') {
    return false
  }
  if (chat.discussion_mode === 'topics') {
    return Boolean(chat.space_room_id?.trim())
      && chat.transition_status !== 'provisioning'
  }
  return chat.transition_status !== 'provisioning'
}

export function getChatPreparationMessage(chat: Parameters<typeof isBackendRoomReady>[0]): string {
  if (!chat) {
    return ''
  }
  if (chat.transition_status === 'failed') {
    return 'Не удалось подготовить чат. Можно повторить подключение.'
  }
  const preparation = (chat as IChat | null)?.preparation
  if (preparation?.status === 'retry') {
    return 'Подключение временно отложено. Повторим автоматически.'
  }
  return isBackendRoomReady(chat) ? '' : 'Подготовка чата…'
}

export function isChatProvisioning(
  chat: Pick<IChat, 'id' | 'matrix_room_id' | 'space_room_id' | 'discussion_mode' | 'transition_status'> | null | undefined,
): boolean {
  return Boolean(chat?.id) && !isBackendRoomReady(chat)
}

function matrixMembershipForRoom(roomId: string): 'join' | 'other' | 'missing' | 'offline' {
  try {
    if (!isMatrixClientActive()) {
      return 'offline'
    }
    const client = getMatrixClient()
    const room = client.getRoom(roomId)
    if (!room) {
      return 'missing'
    }
    return room.getMyMembership() === 'join' ? 'join' : 'other'
  } catch {
    return 'offline'
  }
}

export function resolveChatReadyState(options: {
  chat: IChat | null | undefined
  activeTopic?: IChatTopic | null
  showTopicsList?: boolean
  /** Если false — не требуем join в SDK (только backend room). По умолчанию true. */
  requireMatrixJoin?: boolean
}): ChatReadyState {
  const {
    chat,
    activeTopic = null,
    showTopicsList = false,
    requireMatrixJoin = true,
  } = options

  if (!chat?.id) {
    return {
      status: 'creating',
      canCompose: false,
      message: 'Подключение чата…',
    }
  }

  const availability = activeTopic?.availability ?? chat.availability
  if (availability?.status === 'unavailable') {
    return {
      status: 'failed',
      canCompose: false,
      message: activeTopic
        ? 'Тема недоступна. Сообщите администратору.'
        : 'Чат недоступен. Сообщите администратору.',
    }
  }

  if (chat.transition_status === 'failed') {
    return {
      status: 'failed',
      canCompose: false,
      message: 'Не удалось подготовить чат. Обновите или повторите позже.',
    }
  }

  if (!isBackendRoomReady(chat)) {
    return {
      status: 'creating',
      canCompose: false,
      message: getChatPreparationMessage(chat),
    }
  }

  if (showTopicsList && isTopicsModeChat(chat) && !activeTopic) {
    return {
      status: 'creating',
      canCompose: false,
      message: 'Выберите тему',
      roomId: chat.matrix_room_id?.trim(),
    }
  }

  if (activeTopic && !activeTopic.matrix_room_id?.trim()) {
    return {
      status: 'creating',
      canCompose: false,
      message: 'Подготовка темы…',
      roomId: chat.matrix_room_id?.trim(),
    }
  }

  const roomId = (
    activeTopic?.matrix_room_id?.trim()
    || chat.matrix_room_id?.trim()
    || ''
  )

  if (!roomId) {
    return {
      status: 'creating',
      canCompose: false,
      message: getChatPreparationMessage(chat),
    }
  }

  let userId: string | null | undefined
  try {
    userId = getMatrixClient().getUserId?.()
  } catch {
    userId = undefined
  }
  const operation = getMembershipStatus(roomId, userId)
  if (operation?.status === 'failed' || operation?.status === 'left') {
    return { status: 'failed', canCompose: false, message: membershipFailureMessage(operation.error_code), roomId }
  }

  if (!requireMatrixJoin) {
    return {
      status: 'ready',
      canCompose: true,
      message: '',
      roomId,
    }
  }

  const membership = matrixMembershipForRoom(roomId)
  if (membership === 'offline') {
    return {
      status: 'degraded',
      canCompose: false,
      message: 'Нет связи с чатом…',
      roomId,
    }
  }
  if (membership === 'missing' || membership === 'other') {
    const delayed = operation?.status === 'pending' && operation.retry_at
      && Date.parse(operation.retry_at) > Date.now() + 15_000
    return {
      status: 'joining',
      canCompose: false,
      message: delayed ? 'Подключение отложено. Повторим автоматически.' : 'Подключаем к комнате…',
      roomId,
    }
  }

  return {
    status: 'ready',
    canCompose: true,
    message: '',
    roomId,
  }
}

/** Join ещё идёт — ленту уже можно показать, композер нет. */
export function canShowChatTimeline(status: ChatReadyStatus): boolean {
  return status === 'ready' || status === 'joining'
}
